import { useEffect, useRef } from 'react';
import {
  PHONE_DIAL_CODE_COMMON_OPTIONS,
  PHONE_DIAL_CODE_OTHER_OPTIONS,
  deleteDigitBeforeCaret,
  detectCountryFromInternational,
  phoneExampleForCountry,
  reformatPhoneWithCaret,
  type CountryCode,
} from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { FieldStatusMarker, SavedIndicator } from './FieldStatusMarker';

/**
 * SIL-1198 (original), SIL-1574 (extracted from apps/customer/src/routes/
 * individual-profile.tsx into this shared component, no behavior change,
 * so BuyerVerificationModal can reuse it) — phone number field: a dial-code
 * selector (own label, "Country dial code") + a live-formatted
 * national-format input, `inputmode="tel"` / `autocomplete="tel"`.
 * Formatting/validation is 100% `libphonenumber-js` (via
 * packages/shared/src/phone.ts) — this component owns no country rules of
 * its own, per the SIL-1198 issue's decision.
 *
 * Caret preservation: `reformatPhoneWithCaret` computes both the
 * reformatted text and where the caret belongs in it; the caret itself is
 * restored imperatively via `inputRef` in a `useEffect` keyed on `value`,
 * since React's controlled-input re-render otherwise resets it to the end.
 * A keystroke `AsYouType` can't use (a stray letter) never reaches the
 * digit stream `reformatPhoneWithCaret` builds from, so it disappears
 * without any error flash — no per-keystroke validation runs here at all;
 * that only happens on blur (`onBlur`, wired by the caller) or once the
 * caller judges the value "plausible length".
 *
 * `hideHeader` (SIL-1574): skips the top "Phone" label + required/complete/
 * invalid status marker + autosave indicator row, for a caller (e.g.
 * BuyerVerificationModal) that already renders its own label/hint/error
 * chrome around this field. Default `false` keeps individual-profile's
 * original render exactly as it was before the extraction.
 */
export function PhoneField({
  value,
  country,
  onCountryChange,
  onChange,
  onBlur,
  required,
  complete,
  invalid,
  errorMessage,
  attention,
  saving,
  saved,
  hideHeader = false,
  label = 'Phone',
  id = 'field-phone',
  dialId = 'field-phoneCountry',
}: {
  value: string;
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  onChange: (formatted: string) => void;
  onBlur: () => void;
  required?: boolean;
  complete?: boolean;
  invalid?: boolean;
  errorMessage?: string;
  attention?: boolean;
  saving?: boolean;
  saved?: boolean;
  hideHeader?: boolean;
  label?: string;
  id?: string;
  dialId?: string;
}) {
  // SIL-1212: a real, validator-accepted example ("(201) 555-0123") instead
  // of the old fictional "(555) 000-0000" — isValidPhoneNumber rejects that
  // placeholder for every country, so the field's own example failed its
  // own validation. Recomputed per selected country.
  const placeholder = phoneExampleForCountry(country) ?? 'Enter your phone number';
  const markerId = `${id}-marker`;
  const errorId = `${id}-error`;
  const isInvalid = Boolean(invalid);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaretRef = useRef<number | null>(null);

  useEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret != null && inputRef.current) {
      inputRef.current.setSelectionRange(caret, caret);
      pendingCaretRef.current = null;
    }
  }, [value]);

  return (
    <div>
      {hideHeader ? null : (
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
            {label}
          </label>
          <FieldStatusMarker
            id={markerId}
            required={Boolean(required)}
            complete={Boolean(complete)}
            invalid={isInvalid}
            attention={Boolean(attention)}
          />
          <SavedIndicator saving={saving} saved={saved} />
        </div>
      )}
      <div className="flex gap-2">
        {/*
          SIL-1212: w-40 (160px) cut "🇺🇸 United States +1" off mid-name, so
          the closed select never showed the dial code at all. Two fixes
          together close the gap: PHONE_DIAL_CODE_OPTIONS now orders each
          option's label flag-then-code-then-name (phone.ts), so the code is
          never the part that gets truncated; and this box is wide enough to
          show flag + code in full for every country, with the (often much
          longer) name free to truncate behind it. Common countries are a
          separate, un-alphabetized <optgroup> pinned above the full list.
        */}
        <div className="w-56 shrink-0">
          <label htmlFor={dialId} className="mb-1 block text-2xs font-medium text-muted-foreground">
            Country dial code
          </label>
          <select
            id={dialId}
            value={country}
            onChange={(e) => onCountryChange(e.target.value as CountryCode)}
            autoComplete="tel-country-code"
            className="w-full rounded-md border border-border bg-card px-2 py-2 text-sm text-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <optgroup label="Common">
              {PHONE_DIAL_CODE_COMMON_OPTIONS.map((o) => (
                <option key={o.country} value={o.country}>
                  {o.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="All countries">
              {PHONE_DIAL_CODE_OTHER_OPTIONS.map((o) => (
                <option key={o.country} value={o.country}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        {/*
          Raw <input>, not the shared TextInput — TextInput isn't ref-
          forwarding, and caret restoration after each reformat (see the
          useEffect above) needs a real element ref. className mirrors
          TextInput's own styling so this stays visually identical to
          every other field on the page.
        */}
        <input
          id={id}
          ref={inputRef}
          className={cn(
            'w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-muted disabled:text-muted-foreground',
            'focus:ring-2 placeholder:text-muted-foreground text-foreground flex-1',
          )}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={value}
          onChange={(e) => {
            const el = e.target;
            const caret = el.selectionStart ?? el.value.length;
            const { formatted, caret: nextCaret } = reformatPhoneWithCaret(el.value, caret, country);
            pendingCaretRef.current = nextCaret;
            onChange(formatted);
          }}
          onKeyDown={(e) => {
            // SIL-1212: backspace over formatting punctuation (')', '-', a
            // space) used to delete nothing a user could see — the digit
            // string was unchanged, so reformatting produced the same text
            // back, and it took a second backspace (now landing on a real
            // digit) to actually remove one. Intercepting Backspace here and
            // always deleting the nearest digit before the caret
            // (deleteDigitBeforeCaret) makes one keystroke = one digit
            // removed, every time. Only for a collapsed caret — a real
            // range selection (Shift+Backspace over highlighted text) is
            // left to the browser's default delete-the-selection behavior.
            if (e.key !== 'Backspace') return;
            const el = e.currentTarget;
            if (el.selectionStart == null || el.selectionStart !== el.selectionEnd) return;
            const caret = el.selectionStart;
            if (caret === 0) return;
            e.preventDefault();
            const { value: nextRaw, caret: rawCaret } = deleteDigitBeforeCaret(el.value, caret);
            const { formatted, caret: nextCaret } = reformatPhoneWithCaret(nextRaw, rawCaret, country);
            pendingCaretRef.current = nextCaret;
            onChange(formatted);
          }}
          onPaste={(e) => {
            // SIL-1212: a pasted "+CC…" number carries its own country —
            // switch the dial-code selector to match rather than leaving it
            // on whatever was previously selected (which silently mis-
            // formats/mis-validates the pasted digits against the wrong
            // country's rules). Detect from the clipboard text directly
            // (not el.value, which hasn't been updated with the paste yet
            // inside a 'paste' handler) and build the reformatted result
            // against the DETECTED country in this same handler — deferring
            // to the input's own onChange would still read the stale
            // `country` prop from this render's closure.
            const pasted = e.clipboardData?.getData('text') ?? '';
            const detected = detectCountryFromInternational(pasted);
            if (!detected) return;
            e.preventDefault();
            const el = e.currentTarget;
            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            const nextRaw = el.value.slice(0, start) + pasted + el.value.slice(end);
            const rawCaret = start + pasted.length;
            const { formatted, caret: nextCaret } = reformatPhoneWithCaret(nextRaw, rawCaret, detected);
            pendingCaretRef.current = nextCaret;
            onCountryChange(detected);
            onChange(formatted);
          }}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-required={required}
          aria-invalid={isInvalid || undefined}
          aria-describedby={
            hideHeader
              ? isInvalid
                ? errorId
                : undefined
              : isInvalid
                ? `${markerId} ${errorId}`
                : markerId
          }
        />
      </div>
      {isInvalid && errorMessage && (
        <p id={errorId} className="mt-1 text-xs text-status-red">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
